// Considered: ContextShellOrganism — rejected as a literal wrapper (plan OQ-4). It is a
// Context/TreeMap-specific render-prop shell (context selection, ContextView resolution, a
// ViewStateBoundary session layer, filters). Its STRUCTURAL pattern is followed here instead: a
// topbar/canvas/side-panel layout built from canonical atoms, no bespoke chrome components.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { SpaceSettings, SpaceWire } from 'types/space';
import type { Sphere } from 'types/global';
import { computeComponentTreeDepth, DEFAULT_SPACE_DOCUMENT_LIMITS } from '@center-platform/space-format/validate';
import type { ComposerState, ContextViewBinding, GhostProposal } from '../../../composer/composerStore';
import type { GridPosition } from '../../../composer/gridPlacement';
import type { SelectedNode, SpaceAction } from '../../../composition-host/types';
import { filterOptionsByClosureContexts, type ClosureLintFinding } from '../../../composer/closureLint';
import { SPACE_CATALOG_ENTRIES, findSourceCatalogEntry, isWriteShapedAction } from '../../../composer/actionClassification';
import { deriveActionLogEntries, type ComposerActionLogEntry } from '../../../composer/actionLog';
import { SpaceBusProvider, useSpaceWires } from '../../../composition-host/spaceBus';
import type { ComposerAgentScope, ComposerAgentTarget } from '../../../composer/agentTransport';
import type { SphereSearchResult } from '../../../hooks/useSphereSearch';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useAvailableContexts } from '../../../hooks/useAvailableContexts';
import { getContextViewsByContext, type ContextViewRecord } from '../../../api/contextApi';
import { resolveLabel } from '../../../utils/resolveLabel';
import { searchComposables, type ComposablesSearchResult } from '../../../api/marketplaceApi';
import type { ComposerTargetKind } from '@center-platform/component-catalog';
import ComposerCanvasOrganism, { type ComposerCanvasViewMode } from './ComposerCanvasOrganism';
import ComposerSpherePickerOrganism from './ComposerSpherePickerOrganism';
import AgentThreadOrganism from './AgentThreadOrganism';
import RegistryRailOrganism from './RegistryRailOrganism';
import ComposerInspectorMolecule, { type OverrideOption } from '../../molecules/composer/ComposerInspectorMolecule';
import ConformanceFindingsListMolecule from '../../molecules/ConformanceFindingsList/ConformanceFindingsListMolecule';
import type { CompositionHostHandle } from '../../../composition-host/CompositionHost';
import { useComposerCheck } from '../../../composer/useComposerCheck';
import TryBarMolecule, { type TryBarOverrideChip, type TryBarOverrideKind } from '../../molecules/composer/TryBarMolecule';
import ActionLogMolecule from '../../molecules/composer/ActionLogMolecule';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import NotificationDotAtom from '../../atoms/NotificationDot/NotificationDotAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import CustomDialogAtom from '../../atoms/CustomDialog/CustomDialogAtom';
import { confirmDialogAtom } from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import './ComposerShellOrganism.scss';

const MODE_OPTIONS: Array<{ value: ComposerCanvasViewMode; labelKey: string }> = [
  { value: 'compose', labelKey: 'composer.shell.mode.compose' },
  { value: 'try', labelKey: 'composer.shell.mode.try' },
  { value: 'wires', labelKey: 'composer.shell.mode.wires' },
];

export interface ComposerShellOrganismProps {
  state: ComposerState;
  apiClient?: ApiClient;
  /**
   * `search-service`'s client — the registry rail's `search_composables` reads are not on
   * `apiClient` (schema-service). Absent renders the rail without live results.
   */
  searchApiClient?: ApiClient;
  /** Fires for the registry rail's "open in composer" action on a composition-backed result. */
  onOpenComposableInPlayground?: (systemName: string) => void;
  /** Fires for the registry rail's "fork" action. Absent hides the action. */
  onForkComposable?: (systemName: string) => void;
  /**
   * What kind of thing this shell is composing (EAINT-844 Phase F4 "Grammar") — constrains the
   * registry rail (and, transitively via `agentScope`'s caller, the agent's own search) to what
   * may legally be PLACED into this target. Defaults to `'space'` — the only live composer
   * target today (OQ-8).
   */
  targetKind?: ComposerTargetKind;
  /** Fires when the rail's "+ Add" affordance places a result into the draft. Absent (or `locked`/read-only) hides Add. */
  onAddFromRail?: (result: ComposablesSearchResult) => void;
  /** Fires when the topbar surface-swap control's confirmation is accepted. Absent (or `locked`/read-only) hides the surface swap affordance. */
  onSwapSurface?: (component: string) => void;
  /**
   * Seeds the anchor sphere before the caller (or the user, via the topbar picker) has one — the
   * shell owns the pick from here on (SC4: composer-local state, never `setSphereId`/navigation).
   */
  sphereId?: string;
  /** The draft row's numeric version (SC3's save-conflict `expectedVersion`) — absent while loading. */
  draftVersion?: number | null;
  title: string;
  selectedNode: SelectedNode | null;
  onSelect: (node: SelectedNode | null) => void;
  /** Resolves and selects this component id once the canvas can resolve it (EAINT-865 gap 14 — `?node=` deep links from finding rows). Absent selects nothing on mount. */
  initialSelectedNodeId?: string;
  /** Absent when the caller lacks `edit` — the shell renders read-only with no Save affordance. */
  onSave?: () => void;
  saving?: boolean;
  onBack?: () => void;
  /** Absent outside approach scope, or when the caller lacks `edit` — no Publish affordance renders. */
  onPublish?: () => void;
  publishing?: boolean;
  /**
   * Approach-scope closure lint findings (SC7) — out-of-closure composition/context refs the
   * draft binds. Rendered as a chip strip below the topbar. Absent (or empty) outside approach
   * scope.
   */
  lintFindings?: ClosureLintFinding[];
  /** Fires for a `composition`-typed finding's "add to approach" chip action. */
  onAddToApproach?: (finding: ClosureLintFinding) => void;
  /**
   * Approach-scope closure context members (SC7) — narrows the inspector's session-override
   * `context` picker (and transitively its `view` picker, since views are only fetched for a
   * selected context) to contexts the closure actually declares. Undefined (org scope, or the
   * closure hasn't loaded yet) leaves every sphere-available context selectable.
   */
  closureContextSystemNames?: string[];
  /** Try mode's viewer-as candidates (SC4). Empty renders the switcher disabled. */
  perspectives?: Sphere[];
  /** Fires when a session override is newly applied (never on clear) — analytics hook. */
  onOverrideApplied?: (overrideKind: TryBarOverrideKind) => void;
  /** Clears one component's session context/view override. Absent when the caller lacks `edit`. */
  onClearOverride?: (componentId: string) => void;
  onClearAllOverrides?: () => void;
  /**
   * Sets/updates a component's session-only context or view override (SC4). Absent when the
   * caller lacks `edit` — the inspector's override lane renders read-only status only.
   */
  onSetOverride?: (componentId: string, binding: ContextViewBinding) => void;
  /** Promotes a component's session override into a real draft edit. Absent when the caller lacks `edit`. */
  onApplyOverrideToDocument?: (componentId: string) => void;
  /** Adds/removes a declared wire in Wires mode (SC5, SC6). Absent when the caller lacks `edit`. */
  onAddWire?: (wire: SpaceWire) => void;
  onRemoveWire?: (wireId: string) => void;
  /** Precise numeric x/y/w/h editing + Compose-mode drag/resize (EAINT-844 Phase F5a). Absent when the caller lacks `edit`. */
  onSetGridPosition?: (componentId: string, gridPosition: GridPosition) => void;
  /** The Space's header fields (EAINT-844 Phase F5b) — not part of `state.draft`, so they're threaded separately. */
  spaceName?: string;
  spaceDescription?: string;
  onNameChange?: (name: string) => void;
  onDescriptionChange?: (description: string) => void;
  /** Patches `state.draft.settings` (toolbar enabled/items/default, theme, columns). Absent when the caller lacks `edit`. */
  onSettingsChange?: (patch: Partial<SpaceSettings>) => void;
  /**
   * Agent thread (SC8). Absent `agentTarget` skips mounting the thread entirely (e.g. the caller
   * hasn't loaded a target id yet); `agentEndpoint` absent still mounts it in its unavailable state
   * so the Agent/Inspect tab is visible from the first render.
   */
  agentEndpoint?: string;
  agentGetToken?: () => Promise<string | undefined>;
  agentTarget?: ComposerAgentTarget;
  agentScope?: ComposerAgentScope;
  agentDraftDigest?: string;
  onProposeGhost?: (ghost: GhostProposal, rationale: string) => void;
  onAcceptGhost?: (ghostId: string) => void;
  onRejectGhost?: (ghostId: string) => void;
  onAgentMessageSent?: () => void;
  onGhostAccepted?: (componentCount: number) => void;
  /** Forwarded to the agent thread — analytics for the `create_context` write path. */
  onContextConfirmed?: (systemName: string) => void;
  onContextCancelled?: () => void;
  onGhostRejected?: (componentCount: number) => void;
  /** "New with agent" deep link (gallery Phase 6) — opens Compose mode with the Agent tab focused. */
  agentFocusOnMount?: boolean;
  /** Opens a composition-backed selected node in the Explorer's playground. Absent hides the button. */
  onOpenInPlayground?: (systemName: string) => void;
  /**
   * True when the target is a code-backed `CATALOG` entry rather than a composition/Space row
   * (EAINT-844 Phase F1 — `/compose/composition/:name` resolving to a catalog type). Swaps the
   * plain read-only badge for an explicit "defined in code" banner. Locked targets should never
   * receive `onSave`/`onPublish`/`onSetOverride`/`onAddWire`/`onForkComposable` in the first
   * place (there is nothing to persist); when one is passed anyway it is discarded here, so the
   * lock holds regardless of the caller. It also makes the distinction visible to the user
   * — a viewer without `edit` on a real Space sees the same canvas differently than a callers
   * viewing an immutable code-backed primitive.
   */
  locked?: boolean;
}

/**
 * The composer shell's layout (SC2): topbar (crumb, dirty indicator, mode tabs, Save/Publish),
 * canvas, and a side panel. The side panel's Inspect/Agent tabs persist across Compose and Try
 * mode — Try mode ADDS the viewer-as/override trybar plus a bottom sandboxed action-log strip
 * (SC5) rather than displacing the inspector/agent thread. Wires mode swaps the side panel for
 * the declared wires list with add/remove editing (SC6). Read-only whenever `onSave` is absent,
 * so a viewer without `edit` gets the same canvas/inspector/Try experience with no writable
 * affordance — Wires-mode editing and override-clearing still require `edit` individually (their
 * callbacks).
 *
 * Wrapped in `SpaceBusProvider` (mirrors `SpaceHost`'s inner/outer split): `useSpaceWires` below
 * needs `useSpaceBus()`, which throws outside a Provider ancestor — the Provider must be a PARENT
 * component, not rendered by the same component that calls the hook.
 */
const ComposerShellInner: React.FC<ComposerShellOrganismProps> = ({
  state,
  apiClient,
  searchApiClient,
  onOpenComposableInPlayground,
  onForkComposable,
  targetKind = 'space',
  onAddFromRail,
  onSwapSurface,
  sphereId,
  draftVersion,
  title,
  selectedNode,
  onSelect,
  initialSelectedNodeId,
  onSave,
  saving = false,
  onBack,
  onPublish,
  publishing = false,
  lintFindings = [],
  onAddToApproach,
  closureContextSystemNames,
  perspectives = [],
  onOverrideApplied,
  onClearOverride,
  onClearAllOverrides,
  onSetOverride,
  onApplyOverrideToDocument,
  onAddWire,
  onRemoveWire,
  onSetGridPosition,
  spaceName,
  spaceDescription,
  onNameChange,
  onDescriptionChange,
  onSettingsChange,
  agentEndpoint,
  agentGetToken,
  agentTarget,
  agentScope,
  agentDraftDigest,
  onProposeGhost,
  onAcceptGhost,
  onRejectGhost,
  onAgentMessageSent,
  onGhostAccepted,
  onContextConfirmed,
  onContextCancelled,
  onGhostRejected,
  agentFocusOnMount = false,
  onOpenInPlayground,
  locked = false,
}) => {
  const { t, i18n } = useTranslation();
  const readOnly = !onSave || locked;

  // A `locked` target is immutable (a code-backed catalog entry — there is nothing to persist).
  // Every mutating affordance is gated on it here, not just the Save/Publish header, so a caller
  // that wires an edit callback through by mistake still gets a genuinely read-only surface.
  const setOverrideHandler = locked ? undefined : onSetOverride;
  const applyOverrideToDocumentHandler = locked ? undefined : onApplyOverrideToDocument;
  const clearOverrideHandler = locked ? undefined : onClearOverride;
  const clearAllOverridesHandler = locked ? undefined : onClearAllOverrides;
  const addWireHandler = locked ? undefined : onAddWire;
  const forkComposableHandler = locked ? undefined : onForkComposable;
  const acceptGhostHandler = locked ? undefined : onAcceptGhost;
  const proposeGhostHandler = locked ? undefined : onProposeGhost;
  const addFromRailHandler = locked ? undefined : onAddFromRail;
  const swapSurfaceHandler = locked ? undefined : onSwapSurface;
  const setGridPositionHandler = locked ? undefined : onSetGridPosition;
  const nameChangeHandler = locked ? undefined : onNameChange;
  const descriptionChangeHandler = locked ? undefined : onDescriptionChange;
  const settingsChangeHandler = locked ? undefined : onSettingsChange;
  const hasLintFindings = lintFindings.length > 0;

  const apiClients = useApiClientsOrNull();
  const [mode, setMode] = useState<ComposerCanvasViewMode>('compose');
  const [anchorSphere, setAnchorSphere] = useState<{ id: string; label: string } | null>(() =>
    sphereId ? { id: sphereId, label: sphereId } : null
  );
  const [spherePickerOpen, setSpherePickerOpen] = useState(false);
  const [surfacePickerOpen, setSurfacePickerOpen] = useState(false);
  const [surfaceOptions, setSurfaceOptions] = useState<ComposablesSearchResult[]>([]);
  const [surfaceOptionsLoading, setSurfaceOptionsLoading] = useState(false);
  const [viewerAs, setViewerAs] = useState<{ id: string; label: string } | null>(null);
  const [actionLog, setActionLog] = useState<ComposerActionLogEntry[]>([]);
  const [wireFrom, setWireFrom] = useState<string>('');
  const [wireAction, setWireAction] = useState<string>('');
  const [wireTo, setWireTo] = useState<string>('');
  // Agent/Inspect tab-share within Compose mode's side panel: selecting a node auto-flips to
  // Inspect, sending a message auto-flips to Agent (spec: "select → Inspect; send → Agent").
  const [sidePanelTab, setSidePanelTab] = useState<'inspect' | 'agent' | 'check'>(agentFocusOnMount ? 'agent' : 'inspect');
  const canvasRef = useRef<CompositionHostHandle>(null);
  const { result: checkResult, checking, runCheck } = useComposerCheck(state.draft, apiClient);
  const logAnalyticsEvent = useAnalytics();

  const wires = state.draft.wires;
  const components = state.draft.a2ui.components;

  // The limits meter surfaces the validator's containment limits live while composing (SC —
  // "discovered while composing, not discovered at publish"), mirroring what
  // `validateSpaceDocument` actually checks: component count, serialized payload size, and
  // tree nesting depth from the document's own root.
  const limits = useMemo(() => {
    // `Blob` measures UTF-8 byte length without `TextEncoder` (absent from this repo's jsdom
    // test environment) and matches `validateSpaceDocument`'s own `Buffer.byteLength(..., 'utf-8')`
    // check. `encodeURIComponent` would also work but throws `URIError` on a lone surrogate, and
    // this runs in the shell's render phase — one malformed prop would blank the whole composer.
    const sizeBytes = new Blob([JSON.stringify(components)]).size;
    const depth = computeComponentTreeDepth(components, state.draft.a2ui.root);
    return {
      componentCount: components.length,
      maxComponents: DEFAULT_SPACE_DOCUMENT_LIMITS.maxComponents,
      sizeKB: Math.round(sizeBytes / 1024),
      maxKB: Math.round(DEFAULT_SPACE_DOCUMENT_LIMITS.maxPayloadBytes / 1024),
      depth,
      maxDepth: DEFAULT_SPACE_DOCUMENT_LIMITS.maxNestingDepth,
    };
  }, [components, state.draft.a2ui.root]);

  const handleFiredAction = useCallback(
    (action: SpaceAction) => {
      const sourceEntry = findSourceCatalogEntry(action.componentId, components, SPACE_CATALOG_ENTRIES);
      const writeShaped = sourceEntry ? isWriteShapedAction(sourceEntry.type, SPACE_CATALOG_ENTRIES) : false;
      const entries = deriveActionLogEntries(action, wires, writeShaped, Date.now());
      setActionLog((prev) => [...prev, ...entries]);
    },
    [components, wires]
  );

  // `useSpaceWires` both routes the action across the Space instance's bus (so a target adapter's
  // `ctx.onWireDelivery` fires, matching runtime — SC6) and forwards it to `handleFiredAction`
  // first, so the log always reflects what actually happened, not what the composer merely intended.
  const composerOnAction = useSpaceWires({ wires, onAction: handleFiredAction });

  const handlePickSphere = useCallback((sphere: SphereSearchResult) => {
    setAnchorSphere({ id: sphere.id, label: sphere.label });
    setSpherePickerOpen(false);
  }, []);

  // A Space's single Surface swaps through its own dedicated control (EAINT-844 Phase F4b) —
  // never through the rail/agent search, which never offer kind 'surface' (registryGrammar.ts).
  // Options are fetched lazily on open, not kept warm, since a surface swap is rare.
  useEffect(() => {
    if (!surfacePickerOpen || !searchApiClient) return undefined;
    let cancelled = false;
    setSurfaceOptionsLoading(true);
    searchComposables(searchApiClient, { kind: 'surface', surface: 'space' })
      .then((response) => {
        if (!cancelled) setSurfaceOptions(response.results);
      })
      .catch(() => {
        if (!cancelled) setSurfaceOptions([]);
      })
      .finally(() => {
        if (!cancelled) setSurfaceOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [surfacePickerOpen, searchApiClient]);

  const handlePickSurface = useCallback(
    (option: ComposablesSearchResult) => {
      setSurfacePickerOpen(false);
      confirmDialogAtom({
        header: t('composer.shell.surfaceSwapConfirmHeader'),
        message: t('composer.shell.surfaceSwapConfirmMessage', { name: option.name }),
        acceptLabel: t('composer.shell.surfaceSwapConfirmAccept'),
        rejectLabel: t('composer.shell.surfaceSwapConfirmReject'),
        accept: () => swapSurfaceHandler?.(option.systemName),
        reject: () => {
          /* dialog dismissal — the current surface is left untouched */
        },
      });
    },
    [t, swapSurfaceHandler]
  );

  const handleViewerSelected = useCallback(
    (perspectiveId: string) => {
      const perspective = perspectives.find((p) => p.id === perspectiveId);
      setViewerAs({ id: perspectiveId, label: perspective?.properties?.name ?? perspectiveId });
      onOverrideApplied?.('viewer');
    },
    [perspectives, onOverrideApplied]
  );

  const overrideChips = useMemo<TryBarOverrideChip[]>(() => {
    const chips: TryBarOverrideChip[] = [];
    Object.entries(state.overrides).forEach(([componentId, override]) => {
      if (override.contextSystemName || override.viewSystemName) {
        chips.push({
          id: componentId,
          kind: 'context',
          label: t('composer.tryMode.overrideChipContext', {
            componentId,
            context: override.contextSystemName,
            view: override.viewSystemName,
          }),
        });
      }
    });
    if (viewerAs) {
      chips.push({ id: '__viewer', kind: 'viewer', label: t('composer.tryMode.overrideChipViewer', { name: viewerAs.label }) });
    }
    return chips;
  }, [state.overrides, viewerAs, t]);

  // A background click (`node === null`) flips to Inspect too, same as selecting a real node
  // (EAINT-844 Phase F5b) — that's what surfaces the Space-settings panel; without this, deselecting
  // while the Agent tab is active would leave the settings panel invisible.
  const handleSelect = useCallback(
    (node: SelectedNode | null) => {
      setSidePanelTab('inspect');
      onSelect(node);
    },
    [onSelect]
  );

  // The composer check panel's own trigger — on-demand, not on every keystroke (a transitive
  // composition-reference warm-up is a real network round-trip). Fires `conformance_check_run`
  // once the same `verifyArtifact` call every other write/publish/check surface runs (SC3)
  // settles, so this panel's usage is measurable the same way publish-blocked/published are.
  const handleRunCheck = useCallback(async () => {
    const { findings, levelsRun, unavailable } = await runCheck();
    // A check that could not run has no verdict to report — emitting `errorCount: 0` here would
    // record a clean check that never happened.
    if (unavailable) return;
    logAnalyticsEvent('conformance_check_run', undefined, {
      artifactType: 'space',
      errorCount: findings.filter((f) => f.severity === 'error').length,
      warningCount: findings.filter((f) => f.severity === 'warning').length,
      levels: levelsRun,
    });
  }, [runCheck, logAnalyticsEvent]);

  // Reuses `CompositionHost`'s own `inspect` handle (SC10) — the same resolution a `?node=`
  // finding-row deep link already uses (EAINT-865 gap 14) — rather than a bespoke lookup, so a
  // finding's path always resolves to whatever the canvas can actually select.
  const handleSelectFinding = useCallback(
    (componentId: string) => {
      const node = canvasRef.current?.inspect(componentId);
      if (node) handleSelect(node);
    },
    [handleSelect]
  );

  const handleAgentMessageSent = useCallback(() => {
    setSidePanelTab('agent');
    onAgentMessageSent?.();
  }, [onAgentMessageSent]);

  const handleClearOverride = useCallback(
    (id: string) => {
      if (id === '__viewer') {
        setViewerAs(null);
        return;
      }
      clearOverrideHandler?.(id);
    },
    [clearOverrideHandler]
  );

  const handleClearAllOverrides = useCallback(() => {
    setViewerAs(null);
    clearAllOverridesHandler?.();
  }, [clearAllOverridesHandler]);

  // Session-override lane (SC4): contexts available on the anchor sphere feed the inspector's
  // override `context` select; only fetched once an anchor sphere is picked and the caller can
  // actually set overrides (`onSetOverride` present — read-only viewers get no lane at all).
  const { contexts: anchorSphereContexts } = useAvailableContexts(
    setOverrideHandler ? apiClients : null,
    anchorSphere?.id
  );
  const overrideContexts = useMemo<OverrideOption[]>(
    () =>
      filterOptionsByClosureContexts(
        anchorSphereContexts.map((c) => ({ systemName: c.system_name, label: resolveLabel(c.name, i18n.language) })),
        closureContextSystemNames
      ),
    [anchorSphereContexts, i18n.language, closureContextSystemNames]
  );

  const selectedOverride = selectedNode ? state.overrides[selectedNode.componentId] : undefined;
  const overrideContextSystemName = selectedOverride?.contextSystemName;
  const [overrideViewsByContext, setOverrideViewsByContext] = useState<Record<string, ContextViewRecord[]>>({});
  const [loadingOverrideViewsFor, setLoadingOverrideViewsFor] = useState<string | null>(null);

  useEffect(() => {
    if (!overrideContextSystemName || !apiClients?.schemaClient || overrideViewsByContext[overrideContextSystemName]) return;
    let cancelled = false;
    setLoadingOverrideViewsFor(overrideContextSystemName);
    getContextViewsByContext(apiClients.schemaClient, overrideContextSystemName)
      .then((views) => {
        if (cancelled) return;
        setOverrideViewsByContext((prev) => ({ ...prev, [overrideContextSystemName]: views }));
      })
      .catch(() => {
        if (!cancelled) setOverrideViewsByContext((prev) => ({ ...prev, [overrideContextSystemName]: [] }));
      })
      .finally(() => {
        if (!cancelled) setLoadingOverrideViewsFor(null);
      });
    return () => {
      cancelled = true;
    };
  }, [overrideContextSystemName, apiClients?.schemaClient, overrideViewsByContext]);

  const overrideViews = useMemo<OverrideOption[]>(() => {
    if (!overrideContextSystemName) return [];
    const views = overrideViewsByContext[overrideContextSystemName] ?? [];
    return views.map((v) => ({ systemName: v.system_name, label: resolveLabel(v.name, i18n.language) }));
  }, [overrideContextSystemName, overrideViewsByContext, i18n.language]);

  const handleOverrideContextChange = useCallback(
    (componentId: string, contextSystemName: string | undefined) => {
      setOverrideHandler?.(componentId, { contextSystemName, viewSystemName: undefined });
      if (contextSystemName) onOverrideApplied?.('context');
    },
    [setOverrideHandler, onOverrideApplied]
  );

  const handleOverrideViewChange = useCallback(
    (componentId: string, viewSystemName: string | undefined) => {
      setOverrideHandler?.(componentId, { viewSystemName });
      if (viewSystemName) onOverrideApplied?.('context');
    },
    [setOverrideHandler, onOverrideApplied]
  );

  const componentOptions = useMemo(
    () => components.map((c) => ({ label: `${c.id} (${c.component})`, value: c.id })),
    [components]
  );

  const handleAddWire = useCallback(() => {
    if (!wireFrom || !wireAction || !wireTo || !addWireHandler) return;
    addWireHandler({ id: crypto.randomUUID(), from: wireFrom, action: wireAction, to: wireTo });
    setWireFrom('');
    setWireAction('');
    setWireTo('');
  }, [wireFrom, wireAction, wireTo, addWireHandler]);

  return (
      <div className="composer-shell" data-testid="composer-shell">
        <div className="composer-shell__topbar">
          <div className="composer-shell__crumb">
            {onBack && (
              <ButtonAtom
                variant="text"
                icon="pi pi-arrow-left"
                aria-label={t('composer.shell.back')}
                onClick={onBack}
              />
            )}
            <span className="composer-shell__title">{title}</span>
            <NotificationDotAtom isUnread={state.dirty} priority="low" />
            {draftVersion != null && (
              <ChipAtom
                className="composer-shell__draft-chip"
                label={t('composer.shell.draftVersion', { version: draftVersion })}
              />
            )}
            {swapSurfaceHandler ? (
              <ButtonAtom
                variant="text"
                size="small"
                className="composer-shell__surface-chip"
                data-testid="composer-shell-surface-swap-trigger"
                onClick={() => setSurfacePickerOpen(true)}
              >
                {t('composer.shell.surfaceLabel', {
                  family: state.draft.surface.family,
                  layout: state.draft.surface.layout,
                })}
              </ButtonAtom>
            ) : (
              <ChipAtom
                className="composer-shell__surface-chip"
                label={t('composer.shell.surfaceLabel', {
                  family: state.draft.surface.family,
                  layout: state.draft.surface.layout,
                })}
              />
            )}
            <ButtonAtom
              variant="text"
              size="small"
              icon="pi pi-map-marker"
              aria-label={t('composer.shell.spherePickerTrigger')}
              data-testid="composer-sphere-picker-trigger"
              onClick={() => setSpherePickerOpen(true)}
            >
              {anchorSphere?.label ?? t('composer.shell.spherePickerPlaceholder')}
            </ButtonAtom>
          </div>
          <SegmentedControlAtom
            options={MODE_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.labelKey) }))}
            value={mode}
            onChange={(value) => setMode(value as ComposerCanvasViewMode)}
            mode="single"
            variant="pills"
            size="small"
            ariaLabel={t('composer.shell.modeSwitcher')}
          />
          <div className="composer-shell__actions">
            <span className="composer-shell__limits" data-testid="composer-shell-limits">
              {t('composer.shell.limits', {
                componentCount: limits.componentCount,
                max: limits.maxComponents,
                sizeKB: limits.sizeKB,
                maxKB: limits.maxKB,
                depth: limits.depth,
                maxDepth: limits.maxDepth,
              })}
            </span>
            {readOnly ? (
              <span className="composer-shell__readonly-badge" data-testid="composer-shell-readonly-badge">
                {t(locked ? 'composer.shell.lockedBadge' : 'composer.shell.readOnly')}
              </span>
            ) : (
              <>
                <ButtonAtom
                  variant="primary"
                  size="small"
                  data-testid="composer-shell-save"
                  disabled={!state.dirty || saving}
                  loading={saving}
                  onClick={onSave}
                >
                  {t('composer.shell.save')}
                </ButtonAtom>
                {onPublish && (
                  <ButtonAtom
                    variant="secondary"
                    size="small"
                    data-testid="composer-shell-publish"
                    disabled={state.dirty || publishing || hasLintFindings}
                    loading={publishing}
                    onClick={onPublish}
                    title={hasLintFindings ? t('composer.shell.publishBlockedByLint') : undefined}
                  >
                    {t('composer.shell.publish')}
                  </ButtonAtom>
                )}
              </>
            )}
          </div>
        </div>
        {locked && (
          <div className="composer-shell__locked-banner" data-testid="composer-shell-locked-banner">
            <ChipAtom icon="pi pi-lock" label={t('composer.shell.lockedBanner')} />
          </div>
        )}
        {mode === 'try' && (
          <TryBarMolecule
            perspectives={perspectives}
            onViewerSelected={handleViewerSelected}
            viewerLabel={viewerAs?.label}
            overrideChips={overrideChips}
            onClearOverride={handleClearOverride}
            onClearAll={overrideChips.length > 0 ? handleClearAllOverrides : undefined}
          />
        )}
        {hasLintFindings && (
          <div className="composer-shell__lint-strip" data-testid="composer-lint-strip">
            {lintFindings.map((finding) => (
              <div className="composer-shell__lint-item" key={`${finding.componentId}:${finding.itemType}:${finding.systemName}`}>
                <ChipAtom
                  label={t('composer.shell.lintOutOfClosure', { systemName: finding.systemName })}
                  icon="pi pi-exclamation-triangle"
                />
                {finding.itemType === 'composition' && onAddToApproach && (
                  <ButtonAtom variant="text" size="small" onClick={() => onAddToApproach(finding)}>
                    {t('composer.shell.addToApproach')}
                  </ButtonAtom>
                )}
              </div>
            ))}
          </div>
        )}
        {/* Try mode is "use the space as a viewer would", so the authoring chrome gets out of the
            way: the registry rail and the inspect/agent panel are unmounted and the canvas takes
            the full width. Compose and Wires keep their rails — you are editing there. */}
        <div className={`composer-shell__body composer-shell__body--${mode}`} data-testid="composer-shell-body">
          {mode !== 'try' && (
          <RegistryRailOrganism
            apiClient={searchApiClient}
            scope={agentScope}
            surface={targetKind === 'composition' ? 'a2ui' : 'space'}
            targetKind={targetKind}
            onAdd={addFromRailHandler}
            onOpenInPlayground={onOpenComposableInPlayground}
            onFork={forkComposableHandler}
          />
          )}
          <div className="composer-shell__canvas">
            <ComposerCanvasOrganism
              ref={canvasRef}
              state={state}
              apiClient={apiClient}
              sphereId={anchorSphere?.id ?? sphereId}
              requiresAnchorSphere={!locked}
              onRequestAnchorSphere={() => setSpherePickerOpen(true)}
              onSelect={handleSelect}
              initialSelectedNodeId={initialSelectedNodeId}
              onAction={mode === 'try' ? composerOnAction : undefined}
              viewMode={mode}
              onGridPositionChange={setGridPositionHandler}
            />
          </div>
          {mode !== 'try' && (
          <div className="composer-shell__side-panel">
            {mode !== 'wires' && (
              <>
                <SegmentedControlAtom
                  className="composer-shell__side-panel-tabs"
                  options={[
                    { value: 'inspect', label: t('composer.shell.sidePanelTab.inspect') },
                    { value: 'agent', label: t('composer.shell.sidePanelTab.agent') },
                    {
                      value: 'check',
                      label: checkResult
                        ? t('composer.shell.sidePanelTab.checkWithCount', { count: checkResult.findings.length })
                        : t('composer.shell.sidePanelTab.check'),
                    },
                  ]}
                  value={sidePanelTab}
                  onChange={(value) => setSidePanelTab(value as 'inspect' | 'agent' | 'check')}
                  mode="single"
                  variant="pills"
                  size="small"
                  ariaLabel={t('composer.shell.sidePanelTab.switcher')}
                />
                <div
                  className={[
                    'composer-shell__side-panel-content',
                    sidePanelTab === 'inspect' ? '' : 'composer-shell__side-panel-content--hidden',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <ComposerInspectorMolecule
                    selectedNode={selectedNode}
                    sessionOverride={setOverrideHandler && selectedNode ? (selectedOverride ?? {}) : undefined}
                    overrideContexts={overrideContexts}
                    overrideViews={overrideViews}
                    overrideViewsLoading={loadingOverrideViewsFor === overrideContextSystemName}
                    onOverrideContextChange={handleOverrideContextChange}
                    onOverrideViewChange={handleOverrideViewChange}
                    onApplyOverrideToDocument={applyOverrideToDocumentHandler}
                    onClearOverride={clearOverrideHandler}
                    onOpenInPlayground={onOpenInPlayground}
                    onSetGridPosition={mode === 'compose' ? setGridPositionHandler : undefined}
                    spaceName={spaceName}
                    spaceDescription={spaceDescription}
                    spaceSettings={state.draft.settings}
                    onNameChange={nameChangeHandler}
                    onDescriptionChange={descriptionChangeHandler}
                    onSettingsChange={settingsChangeHandler}
                  />
                </div>
                <div
                  className={[
                    'composer-shell__side-panel-content',
                    sidePanelTab === 'agent' ? '' : 'composer-shell__side-panel-content--hidden',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {agentTarget && (
                    <AgentThreadOrganism
                      endpoint={agentEndpoint}
                      getToken={agentGetToken}
                      target={agentTarget}
                      scope={agentScope}
                      draftDigest={agentDraftDigest}
                      onProposeGhost={(ghost, rationale) => proposeGhostHandler?.(ghost, rationale)}
                      onAcceptGhost={(ghostId) => acceptGhostHandler?.(ghostId)}
                      onRejectGhost={(ghostId) => onRejectGhost?.(ghostId)}
                      onMessageSent={handleAgentMessageSent}
                      onGhostAccepted={onGhostAccepted}
                      onContextConfirmed={onContextConfirmed}
                      onContextCancelled={onContextCancelled}
                      onGhostRejected={onGhostRejected}
                      focusOnMount={agentFocusOnMount}
                    />
                  )}
                </div>
                <div
                  className={[
                    'composer-shell__side-panel-content',
                    sidePanelTab === 'check' ? '' : 'composer-shell__side-panel-content--hidden',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  data-testid="composer-shell-check-panel"
                >
                  <div className="composer-shell__check-panel">
                    <ButtonAtom
                      variant="secondary"
                      size="small"
                      data-testid="composer-shell-run-check"
                      loading={checking}
                      onClick={handleRunCheck}
                    >
                      {t('composer.shell.check.run')}
                    </ButtonAtom>
                    {checkResult && !checkResult.unavailable && (
                      <span className="composer-shell__check-panel-timestamp">
                        {t('composer.shell.check.lastCheckedAt', {
                          time: new Date(checkResult.checkedAt).toLocaleTimeString(),
                        })}
                      </span>
                    )}
                    {checkResult?.unavailable && (
                      <span
                        className="composer-shell__check-panel-unavailable"
                        role="status"
                        data-testid="composer-shell-check-unavailable"
                      >
                        {t('composer.shell.check.unavailable')}
                      </span>
                    )}
                    <ConformanceFindingsListMolecule
                      findings={checkResult?.findings ?? []}
                      onSelectNode={handleSelectFinding}
                      data-testid="composer-shell-check-findings"
                    />
                  </div>
                </div>
              </>
            )}
            {mode === 'wires' && (
              <div className="composer-shell__wires-panel" data-testid="composer-wires-panel">
                <ul className="composer-shell__wires-list">
                  {(wires ?? []).map((wire) => (
                    <li key={wire.id} className="composer-shell__wires-row">
                      <span>{`${wire.from} · ${wire.action} → ${wire.to}`}</span>
                      {onRemoveWire && (
                        <ButtonAtom
                          variant="text"
                          size="small"
                          icon="pi pi-trash"
                          aria-label={t('composer.wiresMode.removeWire')}
                          onClick={() => onRemoveWire(wire.id)}
                        />
                      )}
                    </li>
                  ))}
                </ul>
                {addWireHandler && (
                  <div className="composer-shell__wires-add" data-testid="composer-wires-add-form">
                    <DropdownAtom
                      value={wireFrom}
                      options={componentOptions}
                      optionLabel="label"
                      optionValue="value"
                      placeholder={t('composer.wiresMode.fromPlaceholder')}
                      onChange={(e) => setWireFrom(e.value)}
                    />
                    <InputAtom
                      value={wireAction}
                      placeholder={t('composer.wiresMode.actionPlaceholder')}
                      onChange={(e) => setWireAction(e.target.value)}
                    />
                    <DropdownAtom
                      value={wireTo}
                      options={componentOptions}
                      optionLabel="label"
                      optionValue="value"
                      placeholder={t('composer.wiresMode.toPlaceholder')}
                      onChange={(e) => setWireTo(e.value)}
                    />
                    <ButtonAtom
                      variant="secondary"
                      size="small"
                      disabled={!wireFrom || !wireAction || !wireTo}
                      onClick={handleAddWire}
                    >
                      {t('composer.wiresMode.addWire')}
                    </ButtonAtom>
                  </div>
                )}
              </div>
            )}
          </div>
          )}
        </div>
        {mode === 'try' && (
          <div className="composer-shell__action-log-strip" data-testid="composer-action-log-strip">
            <ActionLogMolecule entries={actionLog} />
          </div>
        )}
        <ComposerSpherePickerOrganism
          open={spherePickerOpen}
          onClose={() => setSpherePickerOpen(false)}
          onPick={handlePickSphere}
          excludedSphereIds={anchorSphere ? [anchorSphere.id] : undefined}
        />
        {swapSurfaceHandler && (
          <CustomDialogAtom
            header={t('composer.shell.surfaceSwapDialogHeader')}
            visible={surfacePickerOpen}
            onHide={() => setSurfacePickerOpen(false)}
          >
            {surfaceOptionsLoading && <p>{t('composer.rail.loading')}</p>}
            {!surfaceOptionsLoading && surfaceOptions.length === 0 && <p>{t('composer.rail.empty')}</p>}
            <ul className="composer-shell__surface-swap-list" data-testid="composer-shell-surface-swap-list">
              {surfaceOptions.map((option) => (
                <li key={`${option.origin}:${option.systemName}`}>
                  <ButtonAtom
                    variant="text"
                    size="small"
                    data-testid={`composer-shell-surface-swap-option-${option.systemName}`}
                    onClick={() => handlePickSurface(option)}
                  >
                    {option.name}
                  </ButtonAtom>
                </li>
              ))}
            </ul>
          </CustomDialogAtom>
        )}
      </div>
  );
};

/** One stable bus instance id per Space instance mounted (mirrors `SpaceHost`'s `instanceIdRef`), never regenerated across re-renders. */
const ComposerShellOrganism: React.FC<ComposerShellOrganismProps> = (props) => {
  const [busInstanceId] = useState(() => `composer-${Math.random().toString(36).slice(2)}`);

  return (
    <SpaceBusProvider instanceId={busInstanceId}>
      <ComposerShellInner {...props} />
    </SpaceBusProvider>
  );
};

export default ComposerShellOrganism;
